/**
 * ComplianceMonitor — Feb 2026 · r-multi-select refresh
 *
 * A filter/search page that surfaces daily compliance violations
 * (hours shortfalls, missed check-ins, missed deadlines, content
 * calendar publishes). Filters — date range, search, department,
 * reporting manager, assignees, and check-types — are all multi-
 * select where relevant, and every check-type ships **on** by
 * default so the page loads with the full picture rather than an
 * empty one. Results render as collapsible accordions grouped
 * by check-type, each with its own count + severity legend, so
 * one glance surfaces every compliance issue in the day.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MultiSelect from "@/components/ui/multi-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Download, Send, Check, Loader2, Settings, X, Search, ChevronDown, Flame, Zap, Clock,
} from "lucide-react";

const CHECK_COLORS = {
  missing_timesheet: "bg-amber-100 text-amber-800 border-amber-200",
  min_hours: "bg-amber-100 text-amber-800 border-amber-200",
  no_plan: "bg-slate-100 text-slate-700 border-slate-200",
  billable_pct: "bg-blue-100 text-blue-700 border-blue-200",
  missing_check_in: "bg-rose-100 text-rose-800 border-rose-200",
  missing_check_out: "bg-rose-100 text-rose-800 border-rose-200",
  attendance_pct: "bg-rose-100 text-rose-800 border-rose-200",
  missed_deadline: "bg-red-100 text-red-800 border-red-200",
  missed_goal: "bg-red-100 text-red-800 border-red-200",
  missed_content_publish: "bg-purple-100 text-purple-800 border-purple-200",
  missed_status_update_pm: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

const CHECK_LABELS = {
  missing_timesheet: "Timesheet not submitted",
  min_hours: "Hours below target",
  no_plan: "Daily plan not filed",
  billable_pct: "Billable % below target",
  missing_check_in: "Check-in missed",
  missing_check_out: "Check-out missed",
  attendance_pct: "Attendance below threshold",
  missed_deadline: "Task deadline missed",
  missed_goal: "Goal deadline missed",
  missed_content_publish: "Content calendar publish missed",
  missed_status_update_pm: "Project status update stale",
};

const ALL_CHECK_KEYS = Object.keys(CHECK_LABELS);

const SEV_TONE = {
  high: "bg-red-500 text-white",
  medium: "bg-amber-400 text-amber-950",
  low: "bg-slate-300 text-slate-800",
};

export default function ComplianceMonitor() {
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms();
  const canConfigure = can("compliance.configure");
  const canAck = can("compliance.acknowledge");
  const yesterday = new Date(Date.now() - 86400e3).toISOString().slice(0, 10);
  // R268ww · Feb 2026 · Default to YESTERDAY (single-day view).
  // Previous 14-day default surfaced old data on every open, forcing
  // users to narrow the range manually. Yesterday is the natural
  // "what happened in my last completed day" window; admins can widen
  // when they need to. The last-sweep indicator up top tells them how
  // fresh the data is so they can re-sweep if it's stale.

  const [dateFrom, setDateFrom] = useState(yesterday);
  const [dateTo, setDateTo] = useState(yesterday);
  const [search, setSearch] = useState("");
  // r-multi-select: every check is ON by default so users see everything
  // rather than an empty page — matches the "one glance" ask.
  const [checkTypes, setCheckTypes] = useState(ALL_CHECK_KEYS);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [managerIds, setManagerIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [includeAck, setIncludeAck] = useState(false);
  // R268zz · Feb 2026 · Filter acked rows by their reason text.
  // e.g. type "holiday" to see everything acked as "Public holiday".
  const [ackNoteSearch, setAckNoteSearch] = useState("");
  const [data, setData] = useState({ rows: [], summary: { by_check: {}, top_offenders: [] }, last_sweep: null });
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sending, setSending] = useState(false);
  // r-run-sweep · Sep-23 · one-click sweep for HR admins so first-time
  // setups (or workspaces with a stale scheduler) can populate the
  // report without waiting until tomorrow's 03:30 UTC cron.
  const [sweeping, setSweeping] = useState(false);
  // r-digest-preview · state for the preview dialog. `open` gates the
  // Dialog, `loading` is the initial fetch, `previews` is the list from
  // the backend, `keep` is the Set of user_ids the admin still wants to
  // send to (starts full), `sending` toggles the confirm button.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [keepIds, setKeepIds] = useState(() => new Set());
  const [expandedPreviewId, setExpandedPreviewId] = useState(null);

  // R268aaa · Feb 2026 · Ack-reason report (top 10, last 30 days).
  // Loaded once per workspace + when the ack-reason report drawer is
  // opened. Cheap query, safe to eagerly fetch alongside the monitor.
  const [ackReasonReport, setAckReasonReport] = useState(null);
  const [showAckReport, setShowAckReport] = useState(false);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/compliance/monitor/ack-reasons?days=30&top=10`)
      .then((r) => setAckReasonReport(r.data))
      .catch(() => setAckReasonReport(null));
  }, [activeWorkspaceId, includeAck]);  // reload after bulk-ack via includeAck toggle

  // Filter-source lookups — departments, employees (for assignees + managers)
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/departments`)
      .then((r) => setDepartments(Array.isArray(r.data) ? r.data : (r.data?.departments || [])))
      .catch(() => setDepartments([]));
    api.get(`/workspaces/${activeWorkspaceId}/employees`)
      .then((r) => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])))
      .catch(() => setEmployees([]));
  }, [activeWorkspaceId]);

  // Options wired to <MultiSelect />. Sorted alpha inside the component.
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name || "—" })),
    [departments]
  );
  const managerOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const e of employees) {
      if (!e.reporting_manager_id) continue;
      if (seen.has(e.reporting_manager_id)) continue;
      const mgr = employees.find((x) => x.id === e.reporting_manager_id);
      if (!mgr) continue;
      seen.add(mgr.id);
      opts.push({ value: mgr.id, label: mgr.full_name || mgr.email_official || mgr.email || "—" });
    }
    return opts;
  }, [employees]);
  const assigneeOptions = useMemo(
    () => employees
      .filter((e) => e.user_id)
      .map((e) => ({
        value: e.user_id,
        label: e.full_name || e.email_official || e.email || "—",
      })),
    [employees]
  );
  const checkTypeOptions = useMemo(
    () => ALL_CHECK_KEYS.map((k) => ({ value: k, label: CHECK_LABELS[k] })),
    []
  );

  const load = () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
    if (search) qs.set("search", search);
    if (checkTypes.length && checkTypes.length !== ALL_CHECK_KEYS.length) {
      qs.set("check_types", checkTypes.join(","));
    }
    if (departmentIds.length) qs.set("department_ids", departmentIds.join(","));
    if (managerIds.length) qs.set("manager_ids", managerIds.join(","));
    if (userIds.length) qs.set("user_ids", userIds.join(","));
    if (includeAck) qs.set("include_ack", "true");
    if (includeAck && ackNoteSearch.trim()) qs.set("ack_note_search", ackNoteSearch.trim());
    api.get(`/workspaces/${activeWorkspaceId}/compliance/monitor?${qs}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [
    activeWorkspaceId, dateFrom, dateTo, search,
    checkTypes, departmentIds, managerIds, userIds, includeAck, ackNoteSearch,
  ]);

  const exportXlsx = async () => {
    try {
      const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
      if (search) qs.set("search", search);
      if (checkTypes.length && checkTypes.length !== ALL_CHECK_KEYS.length) {
        qs.set("check_types", checkTypes.join(","));
      }
      if (departmentIds.length) qs.set("department_ids", departmentIds.join(","));
      if (managerIds.length) qs.set("manager_ids", managerIds.join(","));
      if (userIds.length) qs.set("user_ids", userIds.join(","));
      if (includeAck) qs.set("include_ack", "true");
      if (includeAck && ackNoteSearch.trim()) qs.set("ack_note_search", ackNoteSearch.trim());
      const resp = await api.get(
        `/workspaces/${activeWorkspaceId}/compliance/monitor/export.xlsx?${qs}`,
        { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `compliance-monitor-${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  // R268vv · Feb 2026 · Weekly PDF rollup. Same content as the email
  // digest but printable — for leadership reviews.
  const downloadWeeklyPdf = async () => {
    try {
      const resp = await api.get(
        `/workspaces/${activeWorkspaceId}/compliance/digest/weekly.pdf`,
        { params: { date_from: dateFrom, date_to: dateTo },
          responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance-weekly-${dateFrom}-to-${dateTo}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Weekly PDF downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed")); }
  };

  const sendNow = async () => {
    // r-digest-preview · open the preview dialog first — actual send
    // happens from the dialog's Confirm button so admins can un-select
    // recipients before mails go out.
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setKeepIds(new Set());
    setExpandedPreviewId(null);
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/compliance/digest/preview`,
        { date: dateFrom, date_to: dateTo });
      setPreviewData(r.data || {});
      const initial = new Set(
        (r.data?.previews || [])
          .filter((p) => !p.no_recipients)
          .map((p) => p.user_id)
      );
      setKeepIds(initial);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/workspaces/${activeWorkspaceId}/compliance/digest/send-now`,
        { date: dateFrom, date_to: dateTo, user_ids: Array.from(keepIds) });
      toast.success(`Digest sent · ${r.data?.sent || 0} emails${r.data?.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      setPreviewOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  const runSweepNow = async () => {
    // r-run-sweep · sweep the visible range so admins see fresh data
    // in seconds. Range sweeps run in the background (returns job_id);
    // single-day sweeps return stats inline. The sweep endpoint accepts
    // today's date, so no need to cap to yesterday any more.
    setSweeping(true);
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/compliance/run-sweep?date=${dateFrom}&date_to=${dateTo}`,
      );
      if (r.data?.job_id) {
        toast.success(`Sweep queued · ${r.data.days_total} day${r.data.days_total === 1 ? "" : "s"} running in background`);
        // Poll every 4s for up to ~2 minutes to refresh the monitor
        // once the job finishes.
        const jobId = r.data.job_id;
        let ticks = 0;
        const poll = async () => {
          ticks += 1;
          try {
            const p = await api.get(`/workspaces/${activeWorkspaceId}/compliance/run-sweep/${jobId}`);
            const j = p.data || {};
            if (j.state === "done") {
              const t = j.totals || {};
              toast.success(`Sweep done · ${j.days_done} days · ${t.violations || 0} violations`);
              load();
              return;
            }
            if (ticks < 30) setTimeout(poll, 4000);
          } catch { /* silent — user can refresh manually */ }
        };
        setTimeout(poll, 4000);
      } else {
        const v = r.data?.violations ?? 0;
        toast.success(`Sweep complete · ${r.data?.date} · ${v} violation${v === 1 ? "" : "s"}`);
        load();
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Sweep failed")); }
    finally { setSweeping(false); }
  };

  const ackRow = async (r) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/compliance/monitor/${r.id}/ack`);
      toast.success("Acknowledged");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Ack failed")); }
  };

  // R268yy · Feb 2026 · Rich bulk-ack dialog (replaces the browser
  // `confirm`) so admins can drop a reason on the audit log —
  // "Public holiday", "Server outage", etc. Optional; blank reason
  // still works.
  const [bulkAck, setBulkAck] = useState(null); // {checkKey, label, ids} | null
  const ackSection = (checkKey, ids) => {
    if (!ids || ids.length === 0) return;
    setBulkAck({
      checkKey,
      label: CHECK_LABELS[checkKey] || checkKey,
      ids,
    });
  };

  const rows = data.rows || [];
  // Group rows by check-type for the section-per-check UX.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const key of ALL_CHECK_KEYS) map.set(key, []);
    for (const r of rows) {
      if (!map.has(r.rule)) map.set(r.rule, []);
      map.get(r.rule).push(r);
    }
    return Array.from(map.entries())
      .map(([key, items]) => ({ key, items }))
      // hide empty sections when a specific subset is picked, but still
      // show all-empty grid when everything is ON so users know the
      // day is truly clean.
      .filter(({ key, items }) =>
        (checkTypes.length === ALL_CHECK_KEYS.length) || checkTypes.includes(key) || items.length > 0
      )
      .sort((a, b) => (b.items.length - a.items.length) || a.key.localeCompare(b.key));
  }, [rows, checkTypes]);

  const clearFilters = () => {
    setDepartmentIds([]); setManagerIds([]); setUserIds([]);
    setCheckTypes(ALL_CHECK_KEYS); setIncludeAck(false); setSearch("");
  };
  const activeFilterCount =
    (departmentIds.length ? 1 : 0)
    + (managerIds.length ? 1 : 0)
    + (userIds.length ? 1 : 0)
    + (search ? 1 : 0)
    + (checkTypes.length !== ALL_CHECK_KEYS.length ? 1 : 0);

  return (
    <>
      <PageHeader
        eyebrow="Compliance monitoring"
        title="Daily compliance snapshot"
        description="See who missed timesheets, attendance, deadlines, and content publishes. Circulate a digest with one tap."
        testid="compliance-monitor-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/app/reports/compliance-monitor/streaks">
              <Button size="sm" variant="outline" data-testid="cm-streaks-btn">
                <Flame className="w-4 h-4 mr-2" /> Streak leaderboard
              </Button>
            </Link>
            <Button size="sm" variant="outline" onClick={() => setShowAckReport(true)} data-testid="cm-ack-report-btn">
              <Flame className="w-4 h-4 mr-2" /> Ack reasons
              {ackReasonReport?.total_acked > 0 && (
                <span className="ml-1.5 text-[10px] font-mono opacity-70">
                  {ackReasonReport.total_acked}
                </span>
              )}
            </Button>
            <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="cm-export-btn"><Download className="w-4 h-4 mr-2" /> Export XLSX</Button>
            <Button size="sm" variant="outline" onClick={downloadWeeklyPdf} data-testid="cm-weekly-pdf-btn"><Download className="w-4 h-4 mr-2" /> Weekly PDF</Button>
            {canConfigure && (
              <Button size="sm" variant="outline" onClick={runSweepNow} disabled={sweeping} data-testid="cm-run-sweep-btn">
                {sweeping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Run sweep now
              </Button>
            )}
            {canConfigure && (
              <Button size="sm" variant="outline" onClick={() => setShowSettings(true)} data-testid="cm-settings-btn"><Settings className="w-4 h-4 mr-2" /> Digest settings</Button>
            )}
            {canConfigure && (
              <Button size="sm" onClick={sendNow} disabled={sending} data-testid="cm-send-now-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send digest now
              </Button>
            )}
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        {/* R268ww · Feb 2026 · Last-sweep freshness banner.
            Tells admins how recent the data is + which date it covers
            so they know when to re-sweep. Colour goes amber if the
            data is > 24h old vs "date_to". */}
        <LastSweepBanner meta={data.last_sweep} dateFrom={dateFrom} dateTo={dateTo} />
        {/* Filters row 1 — dates + free-text */}
        <div className="border border-border bg-background p-3 flex flex-wrap items-end gap-3">
          <div><Label className="text-[11px]">From</Label><Input data-testid="cm-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" /></div>
          <div><Label className="text-[11px]">To</Label><Input data-testid="cm-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" /></div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[11px]">Search person</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input data-testid="cm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or email…" className="h-9 pl-7" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-4 text-xs">
            <Switch data-testid="cm-include-ack" checked={includeAck} onCheckedChange={setIncludeAck} /> Include acknowledged
          </div>
          {/* R268zz · Feb 2026 · Filter acked rows by their reason.
              Only visible when Include-acknowledged is on. Answers
              "which rows were acked as 'Public holiday'?" — case-
              insensitive substring match on the audit note. */}
          {includeAck && (
            <div className="flex-1 min-w-[180px]">
              <Label className="text-[11px]">Ack reason contains</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  data-testid="cm-ack-note-search"
                  value={ackNoteSearch}
                  onChange={(e) => setAckNoteSearch(e.target.value)}
                  placeholder="e.g. holiday"
                  className="h-9 pl-7"
                />
              </div>
            </div>
          )}
          {activeFilterCount > 0 && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="cm-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear ({activeFilterCount})
            </Button>
          )}
        </div>

        {/* Filters row 2 — multiselects */}
        <div className="border border-border bg-background p-3 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] block mb-1">Department</Label>
            <MultiSelect
              options={departmentOptions}
              value={departmentIds}
              onChange={setDepartmentIds}
              allLabel="All departments"
              placeholder="Filter by department…"
              width="w-56"
              testid="cm-dept-filter"
            />
          </div>
          <div>
            <Label className="text-[11px] block mb-1">Reporting manager</Label>
            <MultiSelect
              options={managerOptions}
              value={managerIds}
              onChange={setManagerIds}
              allLabel="All managers"
              placeholder="Filter by manager…"
              width="w-56"
              testid="cm-mgr-filter"
            />
          </div>
          <div>
            <Label className="text-[11px] block mb-1">Assignees</Label>
            <MultiSelect
              options={assigneeOptions}
              value={userIds}
              onChange={setUserIds}
              allLabel="All employees"
              placeholder="Filter by person…"
              width="w-56"
              testid="cm-assignee-filter"
            />
          </div>
          <div>
            <Label className="text-[11px] block mb-1">Checks {checkTypes.length !== ALL_CHECK_KEYS.length ? `(${checkTypes.length}/${ALL_CHECK_KEYS.length})` : "(all)"}</Label>
            <MultiSelect
              options={checkTypeOptions}
              value={checkTypes}
              onChange={setCheckTypes}
              allLabel="All checks"
              placeholder="Filter by check…"
              width="w-64"
              testid="cm-check-filter"
            />
          </div>
        </div>

        {/* Content: grouped sections + right rail */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <div className="space-y-3" data-testid="cm-groups">
            {loading ? (
              <div className="border border-border bg-background flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <div className="text-sm">Loading compliance events…</div>
              </div>
            ) : rows.length === 0 ? (
              <div className="border border-border bg-background">
                <EmptyState
                  title="Nothing to show for this range"
                  description={
                    <>
                      No compliance events between <span className="font-mono">{dateFrom}</span> and{" "}
                      <span className="font-mono">{dateTo}</span>.
                      <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                        {canConfigure && (
                          <Button
                            size="sm"
                            onClick={runSweepNow}
                            disabled={sweeping}
                            data-testid="cm-empty-run-sweep"
                          >
                            {sweeping ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
                            Run sweep for this range
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="cm-widen-30"
                          onClick={() => {
                            const d30 = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
                            setDateFrom(d30);
                          }}
                        >
                          Widen to last 30 days
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="cm-widen-90"
                          onClick={() => {
                            const d90 = new Date(Date.now() - 90 * 86400e3).toISOString().slice(0, 10);
                            setDateFrom(d90);
                          }}
                        >
                          Widen to last 90 days
                        </Button>
                        {activeFilterCount > 0 && (
                          <Button size="sm" variant="ghost" data-testid="cm-empty-clear" onClick={clearFilters}>
                            Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 text-[11px] text-muted-foreground">
                        Tip: violations appear the morning after the daily sweep runs. If you just onboarded, run a manual sweep from the Compliance Rulebook.
                      </div>
                    </>
                  }
                />
              </div>
            ) : (
              grouped.map(({ key, items }) => (
                <CheckSection
                  key={key}
                  checkKey={key}
                  label={CHECK_LABELS[key] || key}
                  items={items}
                  canAck={canAck}
                  onAck={ackRow}
                  onBulkAck={ackSection}
                />
              ))
            )}
          </div>

          {/* Right rail */}
          <aside className="space-y-3">
            <div className="border border-border bg-background p-3" data-testid="cm-summary">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">This range</div>
              <div className="text-3xl font-bold">{data.summary?.total || 0}</div>
              <div className="text-xs text-muted-foreground">violations · {rows.filter((r) => r.severity === "high").length} high</div>
            </div>
            <div className="border border-border bg-background p-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">By check</div>
              <ul className="space-y-1">
                {Object.entries(data.summary?.by_check || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-xs">
                    <span className={`inline-block px-1.5 py-0.5 rounded border ${CHECK_COLORS[k] || ""}`}>{CHECK_LABELS[k] || k.replaceAll("_", " ")}</span>
                    <span className="font-mono font-semibold">{v}</span>
                  </li>
                ))}
                {!Object.keys(data.summary?.by_check || {}).length && (
                  <li className="text-xs text-muted-foreground">No violations in range.</li>
                )}
              </ul>
            </div>
            <div className="border border-border bg-background p-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Top offenders</div>
              <ul className="space-y-2">
                {(data.summary?.top_offenders || []).map((p) => (
                  <li key={p.user_id} className="flex items-center gap-2 text-xs">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold">
                      {(p.user_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1 truncate">{p.user_name}</span>
                    <span className="font-mono font-semibold text-red-600">{p.count}</span>
                  </li>
                ))}
                {!(data.summary?.top_offenders || []).length && <li className="text-xs text-muted-foreground">No offenders — nice.</li>}
              </ul>
            </div>
          </aside>
        </div>
      </div>
      {showSettings && (
        <DigestSettingsDialog wsId={activeWorkspaceId} onClose={() => setShowSettings(false)} />
      )}
      <DigestPreviewDialog
        open={previewOpen}
        loading={previewLoading}
        data={previewData}
        keepIds={keepIds}
        wsId={activeWorkspaceId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onToggle={(uid) => setKeepIds((prev) => {
          const n = new Set(prev);
          if (n.has(uid)) n.delete(uid); else n.add(uid);
          return n;
        })}
        onToggleAll={(pick) => setPreviewData((d) => {
          if (!d) return d;
          const eligible = (d.previews || []).filter((p) => !p.no_recipients).map((p) => p.user_id);
          setKeepIds(pick ? new Set(eligible) : new Set());
          return d;
        })}
        expandedId={expandedPreviewId}
        onExpand={setExpandedPreviewId}
        sending={sending}
        onCancel={() => setPreviewOpen(false)}
        onConfirm={confirmSend}
      />
      {bulkAck && (
        <BulkAckDialog
          state={bulkAck}
          wsId={activeWorkspaceId}
          onClose={() => setBulkAck(null)}
          onDone={() => { setBulkAck(null); load(); }}
        />
      )}
      {showAckReport && (
        <AckReasonReportDialog
          report={ackReasonReport}
          onClose={() => setShowAckReport(false)}
          onOpenFilter={(reason) => {
            setIncludeAck(true);
            setAckNoteSearch(reason);
            setShowAckReport(false);
          }}
        />
      )}
    </>
  );
}

/* R268ww · Feb 2026 · Freshness banner. Renders a subtle strip above
   the filter bar telling admins when this workspace was last swept —
   "Last swept 4 minutes ago for 2026-02-20 (181 users, 724 violations)".
   Amber warning when the visible range extends beyond the last-swept
   date so admins know to hit "Run sweep now" for the newer days. */
function LastSweepBanner({ meta, dateFrom, dateTo }) {
  if (!meta || !meta.at) {
    return (
      <div className="border border-amber-400/40 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center gap-2"
           data-testid="cm-last-sweep-none">
        <Clock className="w-3.5 h-3.5" />
        No sweep run yet for this workspace. Hit <b>Run sweep now</b> to compute compliance for the visible range.
      </div>
    );
  }
  const at = new Date(meta.at);
  const ageMs = Date.now() - at.getTime();
  const ageMin = Math.round(ageMs / 60000);
  const ageStr =
    ageMin < 1 ? "just now" :
    ageMin < 60 ? `${ageMin} min${ageMin === 1 ? "" : "s"} ago` :
    ageMin < 24 * 60 ? `${Math.round(ageMin / 60)} h${Math.round(ageMin / 60) === 1 ? "" : "rs"} ago` :
    `${Math.round(ageMin / (24 * 60))} d ago`;
  const rangeOutOfDate = dateTo > (meta.date || "");
  const tone = rangeOutOfDate
    ? "border-amber-400/40 bg-amber-50 text-amber-900"
    : "border-emerald-500/30 bg-emerald-50/60 text-emerald-800";
  return (
    <div className={`border ${tone} text-xs px-3 py-2 flex items-center gap-2 flex-wrap`}
         data-testid="cm-last-sweep-banner">
      <Clock className="w-3.5 h-3.5" />
      <span>
        Last swept <b>{ageStr}</b> for <b>{meta.date}</b>
        {typeof meta.evaluated === "number" && (
          <> — {meta.evaluated} user{meta.evaluated === 1 ? "" : "s"} · {meta.violations || 0} violation{meta.violations === 1 ? "" : "s"}</>
        )}
      </span>
      {rangeOutOfDate && (
        <span className="ml-1 font-semibold" data-testid="cm-last-sweep-stale">
          · Selected range extends past this date — re-sweep to include {dateTo}
        </span>
      )}
    </div>
  );
}


/* R268yy · Feb 2026 · Bulk-ack dialog with an optional reason.
   Users see this instead of a browser `confirm()` — the reason lands
   in the audit trail as `acknowledged_note` on every row. Suggested
   chips ("Public holiday", "Server outage", "Data-quality glitch",
   "Approved leave batch") one-tap the field so admins don't retype
   common reasons. Blank reason is allowed — a plain timestamp still
   works. */
function BulkAckDialog({ state, wsId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const suggested = [
    "Public holiday",
    "Server outage",
    "Data-quality glitch",
    "Approved leave batch",
    "System-wide config change",
  ];
  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(
        `/workspaces/${wsId}/compliance/monitor/ack-bulk`,
        { violation_ids: state.ids, note: reason.trim() || undefined });
      toast.success(`Acknowledged ${r.acked || 0} item${r.acked === 1 ? "" : "s"}` +
        (r.skipped ? ` · ${r.skipped} already acked` : ""));
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk ack failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="cm-bulk-ack-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Check className="w-4 h-4" /> Acknowledge {state.ids.length} “{state.label}” item{state.ids.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Add an optional reason so future audits show <b>why</b> these were
            acked. Once saved, the next compliance digest email will
            skip every row you acknowledge.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] block mb-1">Reason (optional)</Label>
            <Input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
              placeholder="e.g. Public holiday not on the calendar yet"
              className="h-9"
              data-testid="cm-bulk-ack-reason"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggested.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setReason(s)}
                className={`text-[11px] px-2 py-1 border rounded-none transition-colors ${
                  reason === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                }`}
                data-testid={`cm-bulk-ack-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-none">Cancel</Button>
          <Button onClick={run} disabled={busy} className="rounded-none" data-testid="cm-bulk-ack-confirm">
            {busy ? "Acknowledging…" : `Ack ${state.ids.length} item${state.ids.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* R268aaa · Feb 2026 · Ack-reason systemic-issue drawer.
   Top-N horizontal bar chart of ack reasons over the last 30 days.
   Clicking a bar jumps into the monitor list pre-filtered by that
   reason (via `onOpenFilter`) so admins go from "40% is Server
   outage" → drilling into the exact rows in one tap. */
function AckReasonReportDialog({ report, onClose, onOpenFilter }) {
  const buckets = report?.buckets || [];
  const grand = report?.total_acked || 0;
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="cm-ack-report-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500" /> Ack reasons — last 30 days
          </DialogTitle>
          <DialogDescription>
            {grand
              ? <>Group the last {grand} acknowledgment{grand === 1 ? "" : "s"} by reason so systemic issues jump out. Click a row to filter the monitor list to just those acks.</>
              : "No acknowledgments in the last 30 days — nothing to summarise yet."}
          </DialogDescription>
        </DialogHeader>
        {buckets.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            <Flame className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No ack reasons to report yet.
          </div>
        )}
        {buckets.length > 0 && (
          <div className="space-y-2">
            {buckets.map((b, i) => {
              const barPct = maxCount ? Math.round((b.count / maxCount) * 100) : 0;
              const isNoReason = b.reason === "(no reason given)";
              return (
                <button
                  key={i}
                  type="button"
                  disabled={isNoReason}
                  onClick={() => !isNoReason && onOpenFilter(b.reason)}
                  className={`w-full text-left border border-border p-2.5 relative overflow-hidden group ${
                    isNoReason ? "opacity-70 cursor-default" : "hover:border-primary/60 cursor-pointer"
                  }`}
                  data-testid={`cm-ack-report-row-${i}`}
                >
                  <div
                    className={`absolute inset-y-0 left-0 ${isNoReason ? "bg-slate-100 dark:bg-slate-800" : "bg-orange-100 dark:bg-orange-950/40"}`}
                    style={{ width: `${barPct}%` }}
                    aria-hidden="true"
                  />
                  <div className="relative flex items-center gap-3">
                    <div className="text-[11px] font-mono opacity-50 w-5 text-right">#{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${isNoReason ? "italic text-muted-foreground" : ""}`}>
                        {b.reason}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{b.count} ack{b.count === 1 ? "" : "s"}</span>
                        <span>{b.reviewer_count} reviewer{b.reviewer_count === 1 ? "" : "s"}</span>
                        {(b.rules || []).slice(0, 3).map((rk) => (
                          <span key={rk} className="opacity-70">· {CHECK_LABELS[rk] || rk}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold font-mono leading-none">{b.percent}%</div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">of acks</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <div className="text-[11px] text-muted-foreground mr-auto">
            Since {report?.since} · updates when you ack more rows.
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckSection({ checkKey, label, items, canAck, onAck, onBulkAck }) {
  const [open, setOpen] = useState(items.length > 0);
  const highs = items.filter((i) => i.severity === "high").length;
  const unackedIds = items.filter((i) => !i.acknowledged && i.id).map((i) => i.id);
  const canBulkAck = canAck && unackedIds.length > 0 && !!onBulkAck;
  return (
    <div className="border border-border bg-background" data-testid={`cm-section-${checkKey}`}>
      <div className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          data-testid={`cm-section-toggle-${checkKey}`}
        >
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium ${CHECK_COLORS[checkKey] || "bg-secondary border-border"}`}>{label}</span>
          <span className={`ml-auto font-mono text-xs font-semibold ${items.length ? "text-red-600" : "text-emerald-600"}`}>
            {items.length}
          </span>
          {highs > 0 && <span className="text-[10px] font-semibold bg-red-500 text-white px-1.5 py-0.5">{highs} HIGH</span>}
        </button>
        {/* R268xx · Feb 2026 · Section-level bulk ack. Useful for
            system-wide misses (holiday, outage) where every user got
            flagged and manually acking one-by-one is painful. Digest
            email query already filters acknowledged rows, so the next
            send skips the whole batch. */}
        {canBulkAck && (
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); onBulkAck(checkKey, unackedIds); }}
            data-testid={`cm-section-bulk-ack-${checkKey}`}
            className="shrink-0 rounded-none text-[11px] h-7"
          >
            <Check className="w-3 h-3 mr-1" /> Ack all {unackedIds.length}
          </Button>
        )}
      </div>
      {open && (
        <div className="border-t border-border overflow-auto">
          {items.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">No issues in this check — 🎉</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-secondary/40">
                <tr className="text-left">
                  <th className="p-2 w-24">Date</th>
                  <th className="p-2">Person</th>
                  <th className="p-2 w-24">Severity</th>
                  <th className="p-2">Detail</th>
                  <th className="p-2 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <ComplianceRow
                    key={r.id || `${r.user_id}-${r.rule}-${r.date}`}
                    r={r}
                    canAck={canAck}
                    onAck={onAck}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function ComplianceRow({ r, canAck, onAck }) {
  return (
    <tr className="border-t border-border hover:bg-secondary/20" data-testid={`cm-row-${r.id || r.user_id + '-' + r.rule}`}>
      <td className="p-2 font-mono text-[11px]">{r.date}</td>
      <td className="p-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold shrink-0">
            {(r.user_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{r.user_name}</div>
            {r.designation && <div className="text-[10px] text-muted-foreground truncate">{r.designation}</div>}
          </div>
        </div>
      </td>
      <td className="p-2">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${SEV_TONE[r.severity] || "bg-secondary"}`}>{r.severity || "medium"}</span>
      </td>
      <td className="p-2 align-top">
        <div className="text-[12px] text-foreground/90 whitespace-normal break-words leading-snug" data-testid={`cm-row-detail-${r.id || r.user_id + '-' + r.rule}`}>
          {r.detail || r.content_title || r.task_title || ""}
        </div>
        {/* R268ww · Feb 2026 · Extra chips (days-late, over-budget)
            underneath the main detail line — helps admins spot the
            most painful items without opening the digest preview.
            R268zz · Feb 2026 · Also surface the ack reviewer + reason
            so the "audit log" view has the who/why at a glance. */}
        {(r.days_late > 0 || r.overshoot_pct || r.priority || r.acknowledged) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {r.days_late > 0 && (
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-900 border border-amber-200">
                {r.days_late} day{r.days_late === 1 ? "" : "s"} late
              </span>
            )}
            {r.overshoot_pct > 0 && (
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-800 border border-red-200">
                +{r.overshoot_pct}% over budget
              </span>
            )}
            {r.priority && (
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-secondary text-foreground/70">
                {r.priority}
              </span>
            )}
            {r.acknowledged && (
              <span
                className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-900 border border-emerald-200"
                title={r.acknowledged_at || ""}
                data-testid={`cm-row-ack-${r.id || r.user_id + '-' + r.rule}`}
              >
                ✓ Acked{r.acknowledged_by_name ? ` by ${r.acknowledged_by_name}` : ""}
                {r.acknowledged_note && (
                  <>
                    {" · "}<b>{r.acknowledged_note}</b>
                  </>
                )}
              </span>
            )}
          </div>
        )}
      </td>
      <td className="p-2 text-right">
        {!r.acknowledged && canAck && (
          <Button size="sm" variant="outline" onClick={() => onAck(r)} data-testid={`cm-ack-${r.id || r.user_id + '-' + r.rule}`}>
            <Check className="w-3 h-3 mr-1" /> Ack
          </Button>
        )}
        {r.acknowledged && (
          <span
            className="text-[11px] text-emerald-600 font-semibold"
            title={
              (r.acknowledged_by_name ? `Acked by ${r.acknowledged_by_name}` : "Acked") +
              (r.acknowledged_at ? ` on ${new Date(r.acknowledged_at).toLocaleString()}` : "") +
              (r.acknowledged_note ? ` · ${r.acknowledged_note}` : "")
            }
          >
            ✓ Acked
          </span>
        )}
      </td>
    </tr>
  );
}

function DigestSettingsDialog({ wsId, onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/compliance/digest-settings`)
      .then((r) => setS(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")));
  }, [wsId]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/compliance/digest-settings`, s.settings);
      toast.success("Saved");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  if (!s) return null;
  const set = (k, v) => setS({ ...s, settings: { ...s.settings, [k]: v } });
  const setRule = (check, key, v) => {
    const per = { ...(s.settings.per_check_recipients || {}) };
    per[check] = { ...(per[check] || {}), [key]: v };
    set("per_check_recipients", per);
  };
  const settings = s.settings;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cm-settings-dialog">
        <DialogHeader>
          <DialogTitle>Compliance digest settings</DialogTitle>
          <DialogDescription>Configure when the daily digest fires + who receives it per check-type.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={!!settings.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="cm-cfg-enabled" />
            <span className="text-sm">Enable daily sweep &amp; digest</span>
          </div>
          {/* r-digest-emails-toggle · Sep-23 · master gate on outbound
              emails. When OFF the sweep still runs (monitor / dashboard
              tile / streak grid stay fresh) but no emails go out —
              admins can observe first, mail later via a manual send. */}
          <div className="flex items-start gap-3 rounded border border-amber-200 bg-amber-50/50 px-3 py-2">
            <Switch
              checked={!!settings.emails_enabled}
              onCheckedChange={(v) => set("emails_enabled", v)}
              data-testid="cm-cfg-emails-enabled"
            />
            <div className="text-xs">
              <div className="font-semibold text-amber-900">Send digest emails automatically</div>
              <div className="text-amber-800/80">
                When OFF, the sweep runs and populates the monitor but no emails are sent. Turn ON only after you're happy with the recipient rules — the daily cron and any "Send digest now" will then go live.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Hour (local, 0-23)</Label>
              <Input type="number" min={0} max={23} value={settings.digest_hour_local} onChange={(e) => set("digest_hour_local", parseInt(e.target.value || "0", 10))} className="h-9" data-testid="cm-cfg-hour" />
            </div>
            <div>
              <Label className="text-[11px]">Minute (0-59)</Label>
              <Input type="number" min={0} max={59} value={settings.digest_minute_local} onChange={(e) => set("digest_minute_local", parseInt(e.target.value || "0", 10))} className="h-9" data-testid="cm-cfg-minute" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!settings.weekly_digest_enabled} onCheckedChange={(v) => set("weekly_digest_enabled", v)} data-testid="cm-cfg-weekly-enabled" />
            <span className="text-sm">Also send weekly digest on Monday</span>
          </div>
          <div>
            <Label className="text-[11px]">Always CC (workspace-wide, comma-separated)</Label>
            <Input value={(settings.global_cc_emails || []).join(", ")} onChange={(e) => set("global_cc_emails", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))} placeholder="compliance@…, ops@…" className="h-9" data-testid="cm-cfg-global-cc" />
          </div>
          {/* r-digest-verbiage · Sep-23 · custom email copy — {name} and
              {first_name} tokens are substituted per recipient. Plain
              text (line breaks preserved) so admins can't accidentally
              break the layout with stray HTML. */}
          <div className="border-t pt-3 space-y-3">
            <div className="text-[11px] uppercase font-semibold text-muted-foreground">Email verbiage</div>
            <div>
              <Label className="text-[11px]">Intro (before the violations list)</Label>
              <textarea
                value={settings.intro_text || ""}
                onChange={(e) => set("intro_text", e.target.value)}
                rows={3}
                placeholder="Here's your compliance snapshot for the last day, {first_name}. Missing anything?"
                className="w-full text-sm px-2 py-1.5 border border-border bg-background"
                data-testid="cm-cfg-intro-text"
              />
            </div>
            <div>
              <Label className="text-[11px]">Outro (after the violations list)</Label>
              <textarea
                value={settings.outro_text || ""}
                onChange={(e) => set("outro_text", e.target.value)}
                rows={3}
                placeholder="Reach out if any of this is a mistake — our Compliance Agent syncs every hour."
                className="w-full text-sm px-2 py-1.5 border border-border bg-background"
                data-testid="cm-cfg-outro-text"
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Tip: use <span className="font-mono">{"{name}"}</span> or <span className="font-mono">{"{first_name}"}</span> to personalise for each recipient.
            </div>
          </div>
          <div className="border-t pt-3 space-y-3">
            <div className="text-[11px] uppercase font-semibold text-muted-foreground">Recipient rules per check</div>
            {s.check_types.map(({ key, label }) => {
              const r = { ...s.defaults, ...(settings.per_check_recipients?.[key] || {}) };
              return (
                <div key={key} className="border border-border p-2" data-testid={`cm-cfg-check-${key}`}>
                  <div className="text-xs font-semibold mb-1">{label}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[11px]">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.individual} onChange={(e) => setRule(key, "individual", e.target.checked)} /> Individual</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.manager} onChange={(e) => setRule(key, "manager", e.target.checked)} /> Manager</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.department_head} onChange={(e) => setRule(key, "department_head", e.target.checked)} /> Dept head</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.hr_team} onChange={(e) => setRule(key, "hr_team", e.target.checked)} /> HR team</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!r.directors} onChange={(e) => setRule(key, "directors", e.target.checked)} /> Directors</label>
                  </div>
                  <Input value={(r.custom_emails || []).join(", ")} onChange={(e) => setRule(key, "custom_emails", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))} placeholder="Extra emails, comma-separated" className="h-8 mt-2 text-[11px]" />
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={onClose}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="cm-cfg-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * r-digest-preview · Sep-23
 *
 * Preview-before-send dialog for the daily compliance digest. Every
 * recipient shows their to/cc/subject + the per-check summary (Timesheet
 * missing · 3, Deadlines · 1, …). Admins un-check anyone they don't want
 * to email; the confirm button posts the keep-list back to `send-now`.
 */
function DigestPreviewDialog({
  open, loading, data, keepIds, onToggle, onToggleAll,
  expandedId, onExpand, sending, onCancel, onConfirm, dateFrom, dateTo,
  wsId,
}) {
  const previews = (data?.previews || []).filter((p) => !p.no_recipients);
  const skippedNoRecipients = (data?.previews || []).filter((p) => p.no_recipients);
  const keepCount = keepIds?.size || 0;
  const allChecked = previews.length > 0 && keepCount === previews.length;
  const [testing, setTesting] = useState(false);
  const sendTestToMe = async () => {
    setTesting(true);
    try {
      // Pick a "sample" recipient — prefer the first one still selected
      // so the admin previews content that resembles what will actually
      // ship. Fall back to the first row if nothing is checked yet.
      const sampleUid =
        previews.find((p) => keepIds.has(p.user_id))?.user_id
        || previews[0]?.user_id;
      const r = await api.post(
        `/workspaces/${wsId}/compliance/digest/send-test`,
        { date: dateFrom, date_to: dateTo, sample_user_id: sampleUid },
      );
      toast.success(`Test sent to ${r.data?.sent_to || "you"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Test send failed")); }
    finally { setTesting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !sending) onCancel(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="cm-digest-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview digest before sending</DialogTitle>
          <DialogDescription>
            Range: <span className="font-mono">{dateFrom}</span> → <span className="font-mono">{dateTo}</span>. Un-tick anyone you don't want to email. Skipped people can still receive the automated digest on the scheduled cron.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <div className="text-sm">Building preview…</div>
          </div>
        ) : previews.length === 0 ? (
          <EmptyState
            title="No one to email"
            description={
              <>
                No open violations landed on any recipient for this range.
                {skippedNoRecipients.length > 0 && (
                  <div className="mt-2 text-xs">
                    {skippedNoRecipients.length} teammate{skippedNoRecipients.length === 1 ? "" : "s"} had violations but no recipient rule matched — configure Digest Settings first.
                  </div>
                )}
              </>
            }
          />
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border pb-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="cm-preview-toggle-all">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="font-semibold">
                  {keepCount} of {previews.length} selected
                </span>
              </label>
              {skippedNoRecipients.length > 0 && (
                <span className="text-muted-foreground">
                  {skippedNoRecipients.length} without recipient rule
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto -mx-6 px-6 divide-y divide-border">
              {previews.map((p) => {
                const kept = keepIds.has(p.user_id);
                const expanded = expandedId === p.user_id;
                return (
                  <div key={p.user_id} className="py-3" data-testid={`cm-preview-row-${p.user_id}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={kept}
                        onChange={() => onToggle(p.user_id)}
                        className="w-4 h-4 mt-1 shrink-0"
                        data-testid={`cm-preview-keep-${p.user_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <div className="font-semibold text-sm">{p.user_name}</div>
                          {p.designation && <div className="text-[11px] text-muted-foreground">{p.designation}</div>}
                          {p.department && <div className="text-[11px] text-muted-foreground">· {p.department}</div>}
                          <div className="ml-auto text-[11px] text-muted-foreground">
                            {p.violation_count} violation{p.violation_count === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="text-xs mt-0.5 space-y-0.5">
                          <div>
                            <span className="text-muted-foreground">To: </span>
                            <span className="font-mono">{p.to || "—"}</span>
                          </div>
                          {p.cc && p.cc.length > 0 && (
                            <div>
                              <span className="text-muted-foreground">Cc: </span>
                              <span className="font-mono">{p.cc.join(", ")}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-muted-foreground">Subject: </span>
                            <span>{p.subject}</span>
                          </div>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(p.sections || []).map((s) => (
                            <span
                              key={s.label}
                              className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 text-[10px] rounded"
                            >
                              {s.label}
                              <span className="font-mono font-semibold">{s.count}</span>
                            </span>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => onExpand(expanded ? null : p.user_id)}
                          className="text-[11px] text-primary hover:underline mt-1.5"
                          data-testid={`cm-preview-expand-${p.user_id}`}
                        >
                          {expanded ? "Hide" : "Show"} email preview
                        </button>
                        {expanded && (
                          <div
                            className="mt-2 border border-border bg-muted/30 p-3 text-xs max-h-64 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: p.html || "" }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          <Button variant="ghost" onClick={onCancel} disabled={sending} data-testid="cm-preview-cancel">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={sendTestToMe}
            disabled={sending || loading || testing || previews.length === 0}
            data-testid="cm-preview-send-test"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send test to me
          </Button>
          <Button
            onClick={onConfirm}
            disabled={sending || loading || keepCount === 0}
            data-testid="cm-preview-confirm"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send to {keepCount} recipient{keepCount === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

